/**
 * Attestor roles registry — defuses the acronym soup ("FUC", "ABCC",
 * "CDIAL", "MOCCAE", "MAPA"...) by attaching a one-sentence role to
 * each orbid. Surfaced via the AttestorLogoSlot hover tooltip and
 * anywhere else an audience needs the human-readable role.
 *
 * Keep entries short — single sentence, no period required. Authority
 * + jurisdiction + what they attest.
 *
 * Add new entries here when scenarios introduce a new attestor.
 */
export type AttestorRole = {
  /** Display name shown in the tooltip header. */
  name: string;
  /** Role: authority + jurisdiction + what they attest. */
  role: string;
};

const ROLES: Record<string, AttestorRole> = {
  // Brazilian chambers + bodies (T1 / T2 export side, T3 import side).
  "orbid:abcc": {
    name: "ABCC — Arab-Brazilian Chamber of Commerce",
    role: "Bilateral trade chamber · attests Brazilian-side exporter standing and certificate-of-origin issuance for BR → Arab-state corridors",
  },
  "orbid:abcc-cert-br": {
    name: "ABCC — Certificate of Origin (BR)",
    role: "Chamber-issued certificate-of-origin authority · BR side of the BR-AE corridor",
  },
  "orbid:cdial": {
    name: "CDIAL Halal",
    role: "Brazilian halal certification body · attests slaughter, processing, packaging meet AE halal-import requirements",
  },
  "orbid:cdial-halal-br": {
    name: "CDIAL Halal · Brazil",
    role: "Issues halal certificates for Brazilian food exports to the UAE under MOCCAE-accepted standards",
  },
  "orbid:mapa-export-br": {
    name: "MAPA — Ministério da Agricultura · Brazil",
    role: "Brazilian Ministry of Agriculture · issues phytosanitary / veterinary certificates for export commodities",
  },

  // UAE-side bodies (T1 / T2 import, T3 export).
  "orbid:fed-uae-chambers": {
    name: "FUC — Federation of UAE Chambers",
    role: "Federation of UAE Chambers · attests origination of goods exported from the UAE; counterpart to ABCC on the AE side",
  },
  "orbid:moccae-ae": {
    name: "MOCCAE — UAE Ministry of Climate Change & Environment",
    role: "UAE federal authority for animal-origin import inspection · accepts CDIAL halal evidence and MAPA phyto certificates",
  },

  // Cross-corridor inspection.
  "orbid:sgs-cargo-ae": {
    name: "SGS — Société Générale de Surveillance (AE branch)",
    role: "Independent cargo-inspection authority · pre-shipment quantity, quality, weight tolerance attestation",
  },
};

/**
 * Resolve an attestor orbid → role description. Returns null when no
 * role is registered (caller falls back to the generic placeholder
 * tooltip). The lookup is forgiving: passes through "orbid:" prefix
 * removal and case normalisation so a scenario JSON typo doesn't
 * silently miss the lookup.
 */
export function attestorRole(orbidOrId: string | null | undefined): AttestorRole | null {
  if (!orbidOrId) return null;
  const key = orbidOrId.toLowerCase();
  if (ROLES[key]) return ROLES[key];
  // Try without orbid: prefix.
  const stripped = key.startsWith("orbid:") ? key : `orbid:${key}`;
  return ROLES[stripped] ?? null;
}
